import {
  Flex,
  Input,
  Steps,
  Text,
  Dropdown,
  AppLoader,
  ChoiceInput,
} from '@components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { companyDetailsFormSchema } from '@validationSchemas/investment';
import WithUser from '@HOCs/withUser';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer } from './Company.styles';
import { WithUserProps } from 'HOCs/withUser';
import { useEffect, useMemo } from 'react';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { InvestmentContainer } from '../Investment.styles';
import { LEGAL_ENTITY_TOTAL_STEPS } from '../Investment.consts';
import {
  useCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
} from '@features/api/companySlice';
import { useCountriesQuery } from '@features/api/appDataSlice';
import { Company } from '@features/api/response/Company';
import { CompanyFormData } from './Company.types';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';
import { UserType } from '@features/api/response/User';

const CompanyPage: React.FC<WithUserProps & { company?: Company }> = ({
  user,
  company,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { state } = useLocation();
  const type = state.type;

  const { data: countries } = useCountriesQuery();

  const [createCompanyMutation] = useCreateCompanyMutation();
  const [updateCompanyMutation] = useUpdateCompanyMutation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<CompanyFormData>({
    resolver: yupResolver(companyDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    context: {
      userEmail: user.email,
    },
    defaultValues: company
      ? {
          name: company.name,
          registrationId: company.registrationId,
          registrationCountry: company.registrationCountry,
          contactEmail: company.contactEmail,
          isCompanyAuthorizedRepresentative: true,
          isCompanyCorrespondenceEmailSame: company.contactEmail === user.email,
        }
      : { ...JSON.parse(localStorage.getItem('companyDetails') ?? '{}') },
  });

  const isAuthorizedRepresentative = watch('isCompanyAuthorizedRepresentative');
  const isCorrespondenceEmailSame = watch('isCompanyCorrespondenceEmailSame');

  useEffect(() => {
    if (isCorrespondenceEmailSame) setValue('contactEmail', undefined);
    if (!isAuthorizedRepresentative)
      setValue('isCompanyCorrespondenceEmailSame', true);
  }, [isCorrespondenceEmailSame, isAuthorizedRepresentative, setValue]);

  const countriesMenuItems = useMemo(() => {
    return (
      countries?.map((x) => ({
        name: x.name,
        value: x.name,
        icon: x.flag,
      })) ?? []
    );
  }, [countries]);

  const onSubmit = async (data: CompanyFormData) => {
    if (!data.isCompanyAuthorizedRepresentative) {
      localStorage.setItem('companyDetails', JSON.stringify(data));
      navigate(`/investment-subscription/${productId}/unavailable`, {
        state: {
          progress: 2,
          type: 'company_representative',
          previousPathName: 'company',
        },
        replace: true,
      });
      return;
    }

    try {
      const payload = {
        name: data.name,
        registrationCountry: data.registrationCountry,
        registrationId: data.registrationId,
        contactEmail: data.contactEmail ?? user.email,
        nomineeCompany: type === 'nominee',
      };
      if (company) {
        await updateCompanyMutation(payload).unwrap();
      } else {
        await createCompanyMutation(payload).unwrap();
      }

      localStorage.removeItem('companyDetails');

      navigate(`/investment-subscription/${productId}/ubos`, {
        state: {
          progress: 4,
        },
        replace: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (!user) return <></>;

  if (company?.detailsLocked) {
    getPersonalDetailLockNavigation(navigate, productId)();
  }

  return (
    <InvestmentContainer data-testid='company-container'>
      <ContentContainer>
        <Steps currentStep={1} totalSteps={LEGAL_ENTITY_TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.company.companyDetails' />
        </Text>

        <Flex flex={1} mt={16} flexDirection='column' gap={12}>
          <Input
            label={intl.formatMessage({
              id: 'investment.company.name',
            })}
            inputProps={{ 'data-testid': 'company-name' }}
            locked={company?.detailsLocked}
            fullWidth
            {...registerInput(register, 'name')}
            {...getErrorProps(intl, errors.name)}
          />
          <Box>
            <Controller
              control={control}
              name='registrationCountry'
              render={({ field }) => (
                <Dropdown
                  label={intl.formatMessage({
                    id: 'investment.company.registrationCountry',
                  })}
                  inputProps={{ 'data-testid': 'registration-company' }}
                  menuItemData={countriesMenuItems}
                  disabled={company?.detailsLocked}
                  {...field}
                />
              )}
            />
          </Box>
          <Input
            label={intl.formatMessage({
              id: 'investment.company.registrationNumber',
            })}
            inputProps={{ 'data-testid': 'company-registration-id' }}
            locked={company?.detailsLocked}
            fullWidth
            {...registerInput(register, 'registrationId')}
            {...getErrorProps(intl, errors.registrationId)}
          />
          <Flex flexDirection='column' gap={6}>
            <Text
              variant='h6'
              fontWeight='fontWeightSemiBold'
              color={theme.palette.black.A100}
            >
              <FormattedMessage id='investment.company.authorizedRepresentative' />
            </Text>
            <Controller
              control={control}
              name='isCompanyAuthorizedRepresentative'
              render={({ field: { ref, value, ...restProps } }) => (
                <ChoiceInput
                  direction='row'
                  fullWidth
                  choices={yesNoChoices}
                  defaultValues={value !== undefined ? [value] : undefined}
                  {...restProps}
                />
              )}
            />
          </Flex>
          {isAuthorizedRepresentative ? (
            <Flex flexDirection='column' gap={6}>
              <Text
                variant='h6'
                fontWeight='fontWeightSemiBold'
                color={theme.palette.black.A100}
              >
                <FormattedMessage
                  id='investment.company.sameEmail'
                  values={{ email: user.email }}
                />
              </Text>
              <Controller
                control={control}
                name='isCompanyCorrespondenceEmailSame'
                render={({ field: { ref, value, ...restProps } }) => (
                  <ChoiceInput
                    direction='row'
                    fullWidth
                    choices={yesNoChoices}
                    defaultValues={value !== undefined ? [value] : undefined}
                    {...restProps}
                  />
                )}
              />
            </Flex>
          ) : null}
          {isAuthorizedRepresentative && isCorrespondenceEmailSame === false ? (
            <Input
              label={intl.formatMessage({
                id: 'investment.company.contactEmail',
              })}
              inputProps={{ 'data-testid': 'company-contact-email' }}
              locked={company?.detailsLocked}
              fullWidth
              {...registerInput(register, 'contactEmail')}
              {...getErrorProps(intl, errors.contactEmail, {
                userEmail: user.email,
              })}
            />
          ) : null}
        </Flex>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/progress`, {
              state: {
                progress: 2,
                type: 'legal_entity',
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

const CompanyWithProps: React.FC<WithUserProps> = ({ user, refetchUser }) => {
  const {
    data: company,
    isLoading,
    isFetching,
  } = useCompanyQuery(undefined, {
    skip:
      user?.userType !== UserType.CompanyHolder &&
      user?.userType === UserType.NomineeCompanyHolder,
  });

  if (isLoading || isFetching) return <AppLoader />;

  return (
    <CompanyPage user={user} refetchUser={refetchUser} company={company} />
  );
};

export default WithUser(CompanyWithProps);
